import React from "react"
import { IContextOptionNormalized } from "../context-options/interfaces/context-options-normalized"
import { useRouter } from "./use-router"
import { useAppSelector } from "../store"
import { getContextOptions } from "../context-options/store/selectors"

const makeCreatePathWithGivenContext =
  (contextOptions: IContextOptionNormalized[]) =>
  (params: { path: string; mainContextId?: string; subContextId?: string }) => {
    const url = new URL(params.path, window.location.origin)
    const userOption = contextOptions.find((option) => option.type === "user")
    if (params.mainContextId) {
      url.searchParams.set("mainContext", params.mainContextId)
      if (params.subContextId) url.searchParams.set("subContext", params.subContextId)
    } else {
      url.searchParams.set("mainContext", userOption?.id || "")
    }
    return `${url.pathname}${url.search}`
  }

const makeCreatePathWithContext =
  (contextOptions: IContextOptionNormalized[], query: Query) =>
  (path: string, options?: { withSubContext: boolean }) => {
    const mainContextId = query.mainContext
    const subContextId = query.subContext

    const shouldNotCreatePathWithSubContext = options?.withSubContext === false || !subContextId
    const createPathWithGivenContext = makeCreatePathWithGivenContext(contextOptions)

    if (shouldNotCreatePathWithSubContext) {
      return createPathWithGivenContext({ path, mainContextId })
    }
    return createPathWithGivenContext({ path, mainContextId, subContextId })
  }

export const useUrlWithContext = () => {
  const contextOptions = useAppSelector(getContextOptions)
  const query = useRouter().query as Query

  const createPathWithGivenContext = React.useMemo(
    () => makeCreatePathWithGivenContext(contextOptions),
    [contextOptions]
  )

  const createPathWithContext = React.useMemo(
    () => makeCreatePathWithContext(contextOptions, query),
    [contextOptions, query]
  )

  return {
    createPathWithGivenContext,
    createPathWithContext,
  }
}

type Query = { mainContext?: string; subContext?: string }
